<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 opacity-75" @click="$emit('close')"></div>
      </div>

      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <h3 class="text-lg font-medium text-gray-900">{{ title }}</h3>
        </div>

        <div class="px-4 py-2">
          <div v-if="error" class="text-red-500 text-sm mb-4">{{ error }}</div>
          <div v-if="uploading" class="text-sm text-gray-600 mb-4">Загрузка...</div>

          <div class="aspect-video relative border rounded-md overflow-hidden bg-gray-100">
            <template v-if="images.length > 0">
              <img
                :src="images[currentSlide]?.url"
                alt="Изображение шага"
                class="w-full h-full object-contain"
              />
              <button
                v-if="images.length > 1"
                @click="prev"
                class="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                v-if="images.length > 1"
                @click="next"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                @click="confirmingDelete = true"
                class="absolute bottom-2 right-2 w-10 h-10 rounded-full bg-red-500/90 hover:bg-red-600 flex items-center justify-center text-white shadow z-10"
                title="Удалить изображение"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <!-- Подтверждение удаления -->
              <div v-if="confirmingDelete" class="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                <div class="bg-white rounded-xl p-6 text-center max-w-sm mx-4">
                  <p class="text-lg font-medium text-gray-900 mb-4">Удалить это изображение?</p>
                  <div class="flex gap-3 justify-center">
                    <button
                      @click="confirmingDelete = false"
                      class="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                    >
                      Отмена
                    </button>
                    <button
                      @click="confirmDelete"
                      class="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
              <span
                class="absolute bottom-2 left-2 z-10 px-2 py-1 rounded bg-white/95 text-sm text-gray-700 font-medium shadow"
              >
                {{ currentSlide + 1 }}/{{ images.length }}
              </span>
            </template>
            <div v-else class="flex items-center justify-center h-full text-gray-500">
              <p>Нет изображений</p>
            </div>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="handleFileChange"
          />
        </div>

        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-2">
          <button
            type="button"
            @click="$emit('close')"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Закрыть
          </button>
          <button
            v-if="!readonly"
            type="button"
            @click="$refs.fileInput?.click()"
            :disabled="uploading"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            Загрузить изображения
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    images: {
      type: Array,
      default: () => []
    },
    error: String,
    uploading: Boolean,
    readonly: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Изображения шага'
    },
    initialSlide: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentSlide: this.initialSlide,
      confirmingDelete: false
    };
  },
  watch: {
    images: {
      handler(val) {
        if (this.currentSlide >= (val?.length ?? 0)) {
          this.currentSlide = Math.max(0, (val?.length ?? 1) - 1);
        }
      }
    }
  },
  methods: {
    prev() {
      this.currentSlide = this.currentSlide <= 0 ? this.images.length - 1 : this.currentSlide - 1;
    },
    next() {
      this.currentSlide = this.currentSlide >= this.images.length - 1 ? 0 : this.currentSlide + 1;
    },
    confirmDelete() {
      this.$emit('remove-image', this.currentSlide);
      this.confirmingDelete = false;
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      const valid = files.filter(f => f.type?.startsWith('image/'));
      if (valid.length) {
        this.$emit('file-change', valid);
      }
    }
  }
};
</script>
